import { useEffect, useState, useCallback } from "react";
import api, { fmtMXNFull } from "@/lib/api";
import { BookOpen, Plus, Pencil, Trash2, X, Search } from "lucide-react";
import { toast } from "sonner";

const UNITS = ["PZA", "SERVICIO", "LITRO", "GARRAFA", "GALON", "JUEGO", "PAR", "LOTE", "KIT"];

export default function WorkshopConceptsTab({ canWrite }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null); // {} nuevo | concepto a editar
  const [f, setF] = useState({ concept: "", unit: "PZA", price: "", public_price: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/workshop/concepts");
    setRows(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((r) => r.concept.toLowerCase().includes(q.toLowerCase()));

  const openNew = () => { setF({ concept: "", unit: "PZA", price: "", public_price: "" }); setOpen({}); };
  const openEdit = (r) => { setF({ concept: r.concept, unit: r.unit, price: r.price, public_price: r.public_price ?? r.price }); setOpen(r); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        concept: f.concept.trim(), unit: f.unit, price: Number(f.price),
        public_price: f.public_price === "" ? null : Number(f.public_price),
      };
      if (open?.id) {
        await api.patch(`/workshop/concepts/${open.id}`, body);
        toast.success("Concepto actualizado");
      } else {
        const { data } = await api.post("/workshop/concepts", body);
        toast.success(data.updated ? "Concepto ya existía — precio actualizado" : "Concepto agregado al catálogo");
      }
      setOpen(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`¿Eliminar "${r.concept}" del catálogo?`)) return;
    try { await api.delete(`/workshop/concepts/${r.id}`); toast.success("Eliminado"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-3" data-testid="workshop-concepts-tab">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5" style={{ color: "var(--text-mute)" }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar concepto…"
            className="w-full input-cyber pl-8 pr-3 py-2 text-sm" data-testid="concepts-search" />
        </div>
        <span className="text-[11px] font-mono" style={{ color: "var(--text-mute)" }}>{filtered.length} conceptos</span>
        {canWrite && (
          <button data-testid="new-concept-btn" onClick={openNew}
            className="btn-primary ml-auto px-3 py-2 rounded-sm text-xs uppercase tracking-widest font-semibold flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> Concepto
          </button>
        )}
      </div>

      <div className="panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]" data-testid="concepts-table">
            <thead className="panel-header">
              <tr className="text-left">
                <th className="label-mono px-4 py-3">Concepto</th>
                <th className="label-mono px-4 py-3">Unidad</th>
                <th className="label-mono px-4 py-3 text-right">Precio interno</th>
                <th className="label-mono px-4 py-3 text-right">Precio público</th>
                <th className="label-mono px-4 py-3 text-right">Utilidad</th>
                {canWrite && <th className="label-mono px-4 py-3 text-right">Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="text-center py-12 text-slate-500">
                  Sin conceptos en el catálogo. Agrégalos aquí o guárdalos desde una cotización con el botón ★.
                </td></tr>
              )}
              {filtered.map((r) => {
                const pub = r.public_price ?? r.price;
                return (
                <tr key={r.id} data-testid={`concept-row-${r.id}`} className="row-hover" style={{ borderBottom: "1px solid var(--border)" }}>
                  <td className="px-4 py-2.5 text-slate-100">{r.concept}</td>
                  <td className="px-4 py-2.5 text-xs font-mono text-slate-400">{r.unit}</td>
                  <td className="px-4 py-2.5 text-right font-mono" style={{ color: "var(--cyan)" }}>{fmtMXNFull(r.price)}</td>
                  <td className="px-4 py-2.5 text-right font-mono" style={{ color: "var(--amber)" }}>{fmtMXNFull(pub)}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-xs" style={{ color: pub - r.price > 0 ? "var(--green)" : "var(--text-mute)" }}>{fmtMXNFull(pub - r.price)}</td>
                  {canWrite && (
                    <td className="px-4 py-2.5 text-right">
                      <button data-testid={`edit-concept-${r.id}`} onClick={() => openEdit(r)} className="p-1.5 rounded-sm hover:bg-white/5" style={{ color: "var(--cyan)" }}><Pencil className="w-4 h-4" /></button>
                      <button data-testid={`delete-concept-${r.id}`} onClick={() => remove(r)} className="p-1.5 rounded-sm hover:bg-white/5" style={{ color: "var(--red)" }}><Trash2 className="w-4 h-4" /></button>
                    </td>
                  )}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {open !== null && (
        <div className="fixed inset-0 z-50 p-4 flex items-center justify-center" style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }} onClick={() => setOpen(null)}>
          <form onSubmit={submit} className="panel-strong w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="concept-form">
            <div className="panel-header px-6 py-4 flex items-center justify-between">
              <div>
                <div className="label-mono">// CATÁLOGO</div>
                <h2 className="font-display font-bold text-xl text-slate-100 flex items-center gap-2"><BookOpen className="w-5 h-5" style={{ color: "var(--cyan)" }} /> {open?.id ? "Editar concepto" : "Nuevo concepto"}</h2>
              </div>
              <button type="button" onClick={() => setOpen(null)} className="text-slate-500 hover:text-cyan-300"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-3">
              <div>
                <label className="label-mono block mb-1.5">Concepto *</label>
                <input required value={f.concept} onChange={(e) => setF({ ...f, concept: e.target.value })}
                  className="w-full input-cyber px-3 py-2 text-sm" data-testid="concept-name" placeholder="CAMBIO DE ACEITE Y FILTRO" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-mono block mb-1.5">Unidad</label>
                  <select value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} className="w-full input-cyber px-3 py-2 text-sm" data-testid="concept-unit">
                    {!UNITS.includes(f.unit) && <option value={f.unit} style={{ background: "#0a0f1d" }}>{f.unit}</option>}
                    {UNITS.map((u) => <option key={u} value={u} style={{ background: "#0a0f1d" }}>{u}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-mono block mb-1.5">Precio interno *</label>
                  <input required type="number" step="0.01" min="0" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })}
                    className="w-full input-cyber px-3 py-2 text-sm text-right" data-testid="concept-price" />
                </div>
              </div>
              <div>
                <label className="label-mono block mb-1.5">Precio público (al cliente)</label>
                <input type="number" step="0.01" min="0" value={f.public_price} onChange={(e) => setF({ ...f, public_price: e.target.value })}
                  className="w-full input-cyber px-3 py-2 text-sm text-right" style={{ borderColor: "rgba(245,158,11,0.4)" }} data-testid="concept-public-price" placeholder="Si lo dejas vacío, se usa el precio interno" />
                {f.price !== "" && f.public_price !== "" && (
                  <div className="text-[11px] font-mono mt-1" style={{ color: "var(--green)" }} data-testid="concept-margin-preview">
                    Utilidad: {fmtMXNFull(Number(f.public_price) - Number(f.price))}
                  </div>
                )}
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={() => setOpen(null)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
                <button type="submit" disabled={saving} data-testid="concept-save" className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm disabled:opacity-50">{saving ? "Guardando…" : "Guardar"}</button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
